import { api } from '@/shared/api';
import { unwrap, unwrapCollection } from '@/shared/api/envelope';

import {
    toInvitation,
    toTeamMember,
    type FieldRole,
    type Invitation,
    type MembershipStatus,
    type TeamMember,
    type TenantRole,
} from './model';
import { invitationResourceSchema, userResourceSchema } from './types';

/*
 * Inloggen, de sessie en `/auth/me` staan in `entities/auth`.
 */

/** Schrijfacties gaan nooit in de offline cache. */
const WRITE = { cacheKey: false } as const;

/** Het maximum van de API per pagina. */
const PER_PAGE = 100;

/**
 * Het team van de **eigen** omgeving (`02-api-contract.md` §Gebruikers en
 * uitnodigingen). Alles werkt op de uuid van de persoon of de uitnodiging.
 *
 * Wat hier terugkomt zijn de mensen met een lidmaatschap van dit bedrijf. Het
 * antwoord bevat bewust géén andere bedrijven van een persoon: dat is
 * bedrijfsgevoelig en alleen zichtbaar in het platformbeheer.
 *
 * Erbij komen gaat altijd via een uitnodiging; er is geen `POST /users`.
 */
/** `GET /users`, alle pagina's, eventueel alleen met één status van het lidmaatschap. */
async function membersWith(status?: MembershipStatus): Promise<TeamMember[]> {
    const members: TeamMember[] = [];
    let page = 1;
    let lastPage = 1;

    do {
        const result = unwrapCollection(
            userResourceSchema,
            await api.get('/users', {
                query: {
                    sort: 'name',
                    per_page: PER_PAGE,
                    page,
                    ...(status ? { 'filter[status]': status } : {}),
                },
            }),
        );

        members.push(...result.items.map(toTeamMember));
        lastPage = result.meta?.last_page ?? page;
        page += 1;
    } while (page <= lastPage);

    return members;
}

export const teamApi = {
    /** `GET /users`, alle pagina's: een team past op één scherm. */
    async members(): Promise<TeamMember[]> {
        return membersWith();
    },

    /**
     * Wie hier nu werkt: alleen een actief lidmaatschap. Alleen zo iemand kan een
     * keuring toegewezen krijgen; de API weigert de rest (422).
     */
    async activeMembers(): Promise<TeamMember[]> {
        return membersWith('active');
    },

    /** `GET /invitations`: alleen wat nog openstaat. */
    async invitations(): Promise<Invitation[]> {
        return unwrapCollection(invitationResourceSchema, await api.get('/invitations')).items.map(
            toInvitation,
        );
    },

    /**
     * Uitnodigen op e-mailadres, met één rol en eventueel de naam van de collega.
     * Het antwoord zegt bewust niet of het adres al een Smartkeur-account heeft:
     * dat is niet aan dit bedrijf.
     */
    async invite(input: { email: string; role: TenantRole; name?: string }): Promise<Invitation> {
        const name = input.name?.trim();

        return toInvitation(
            unwrap(
                invitationResourceSchema,
                await api.post(
                    '/invitations',
                    { email: input.email, role: input.role, ...(name ? { name } : {}) },
                    WRITE,
                ),
            ),
        );
    },

    /** Opnieuw versturen met een nieuwe link; de vorige link werkt niet meer. */
    async resendInvitation(invitationUuid: string): Promise<Invitation> {
        return toInvitation(
            unwrap(
                invitationResourceSchema,
                await api.post(`/invitations/${invitationUuid}/resend`, undefined, WRITE),
            ),
        );
    },

    async withdrawInvitation(invitationUuid: string): Promise<void> {
        await api.delete(`/invitations/${invitationUuid}`, { ...WRITE, expect: 'none' });
    },

    /**
     * `POST /users/me/roles/{role}`: jezelf de rol op locatie erbij geven, zoals
     * de eigenaar die zelf keurt. De API neemt alleen `inspector` aan; je andere
     * rollen blijven. Een rol die je meer laat doen dan nu, geeft de API zo niet
     * (403, met de reden in `detail`).
     */
    async takeOnRole(role: FieldRole): Promise<TeamMember> {
        return toTeamMember(
            unwrap(userResourceSchema, await api.post(`/users/me/roles/${role}`, undefined, WRITE)),
        );
    },

    /** De volledige nieuwe set rollen in dit bedrijf. Niemand wijzigt zijn eigen rollen. */
    async setRoles(userUuid: string, roles: readonly TenantRole[]): Promise<TeamMember> {
        return toTeamMember(
            unwrap(
                userResourceSchema,
                await api.put(`/users/${userUuid}/roles`, { roles: [...roles] }, WRITE),
            ),
        );
    },

    /**
     * Uitschakelen is geen verwijderen: het lidmaatschap hier vervalt, het account
     * en de dossiers op iemands naam blijven bestaan.
     */
    async disable(userUuid: string): Promise<void> {
        await api.delete(`/users/${userUuid}`, { ...WRITE, expect: 'none' });
    },

    /**
     * `POST /users/{user}/enable`: een uitgeschakelde collega weer toelaten, met
     * de rollen die hij had. Zijn sessies komen niet terug; hij logt zelf opnieuw in.
     */
    async enable(userUuid: string): Promise<TeamMember> {
        return toTeamMember(
            unwrap(
                userResourceSchema,
                await api.post(`/users/${userUuid}/enable`, undefined, WRITE),
            ),
        );
    },
};
